import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";
import { apagar, atualizarJson, editarJson, enviarJson, pedirJson } from "./api";

export const CAMINHO_DOS_USUARIOS = "/app/usuarios";
export const CHAVE_DOS_USUARIOS = ["usuarios"] as const;
export const CHAVE_DOS_CONVITES = ["convites"] as const;
export const CHAVE_DO_HISTORICO = ["auditoria", "conta"] as const;
export const CHAVE_DAS_TELAS = ["telas"] as const;

export const esquemaUsuario = z.object({
  id: z.string().min(1),
  email: z.string().min(1),
  nome: z.string().min(1),
  cargo: z.string().nullable(),
  telefone: z.string().nullable(),
  geotask_id: z.string().nullable().default(null),
  superior_id: z.string().nullable().optional(),
  superior_nome: z.string().nullable().optional(),
  organizacao_id: z.string().nullable(),
  interno: z.boolean(),
  ativo: z.boolean(),
  acesso_definido: z.boolean(),
  papeis: z.array(z.string()),
  ultimo_acesso_em: z.string().nullable(),
  criado_em: z.string(),
  /* Segundo fator configurado e confirmado. Ausente (API antiga) vale `false`. */
  mfa_ativo: z.boolean().default(false),
});

const esquemaConvite = z.object({
  id: z.string().min(1),
  email: z.string().min(1),
  papel_chave: z.string().min(1),
  organizacao_id: z.string().nullable(),
  expira_em: z.string(),
  criado_em: z.string(),
});

const esquemaExcecao = z.object({
  permissao_chave: z.string(),
  efeito: z.enum(["grant", "revoke"]),
  motivo: z.string().nullable(),
  registrado_em: z.string(),
});
export type ExcecaoDoUsuario = z.infer<typeof esquemaExcecao>;

const esquemaConviteCriado = z.object({
  id: z.string().min(1),
  email: z.string().min(1),
  papel_chave: z.string().min(1),
  expira_em: z.string(),
  modo_teste: z.boolean(),
});

const esquemaRegistro = z.object({
  id: z.string().min(1),
  ocorrido_em: z.string(),
  ator_email: z.string().nullable(),
  organizacao_id: z.string().nullable(),
  acao: z.string().min(1),
  alvo_tipo: z.string().nullable(),
  alvo_id: z.string().nullable(),
  detalhe: z.record(z.string(), z.unknown()),
  ip: z.string().nullable().optional(),
});

export type UsuarioDaConta = z.infer<typeof esquemaUsuario>;
export type ConviteDaConta = z.infer<typeof esquemaConvite>;
export type RegistroDoHistorico = z.infer<typeof esquemaRegistro>;

export interface NovoConvite {
  nome: string;
  email: string;
  cargo: string | null;
  telefone: string | null;
  papel_chave: string;
}

function invalidarConta(cliente: ReturnType<typeof useQueryClient>) {
  void cliente.invalidateQueries({ queryKey: CHAVE_DOS_USUARIOS });
  void cliente.invalidateQueries({ queryKey: CHAVE_DOS_CONVITES });
  void cliente.invalidateQueries({ queryKey: CHAVE_DO_HISTORICO });
  void cliente.invalidateQueries({ queryKey: CHAVE_DAS_TELAS });
}

export function useUsuariosDaConta() {
  return useQuery({
    queryKey: CHAVE_DOS_USUARIOS,
    queryFn: ({ signal }) =>
      pedirJson("/usuarios?incluir_inativos=true&limite=200", z.array(esquemaUsuario), { signal }),
  });
}

export function useConvitesDaConta() {
  return useQuery({
    queryKey: CHAVE_DOS_CONVITES,
    queryFn: ({ signal }) => pedirJson("/convites", z.array(esquemaConvite), { signal }),
  });
}

export function useHistoricoDaConta() {
  return useQuery({
    queryKey: CHAVE_DO_HISTORICO,
    queryFn: ({ signal }) =>
      pedirJson("/auditoria?limite=500", z.array(esquemaRegistro), { signal }),
  });
}

export function useConvidarUsuario() {
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: (corpo: NovoConvite) => enviarJson("/convites", esquemaConviteCriado, corpo),
    onSuccess: () => invalidarConta(cliente),
  });
}

export function useAlterarPerfil() {
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: ({ usuarioId, papel }: { usuarioId: string; papel: string }) =>
      atualizarJson(`/usuarios/${encodeURIComponent(usuarioId)}/papeis`, esquemaUsuario, {
        papeis: [papel],
      }),
    onSuccess: () => invalidarConta(cliente),
  });
}

/** Troca (ou tira) o superior hierárquico de uma pessoa da própria empresa. */
export function useAlterarSuperior() {
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: ({ usuarioId, superiorId }: { usuarioId: string; superiorId: string | null }) =>
      editarJson(`/usuarios/${encodeURIComponent(usuarioId)}`, esquemaUsuario, {
        superior_id: superiorId,
      }),
    onSuccess: () => invalidarConta(cliente),
  });
}

export function useAlterarAtivacao() {
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: ({ usuarioId, ativo }: { usuarioId: string; ativo: boolean }) =>
      editarJson(`/usuarios/${encodeURIComponent(usuarioId)}`, esquemaUsuario, { ativo }),
    onSuccess: () => invalidarConta(cliente),
  });
}

export function useVincularGeoTask() {
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: ({ usuarioId, email }: { usuarioId: string; email?: string }) =>
      enviarJson(`/usuarios/${encodeURIComponent(usuarioId)}/geotask/buscar`, esquemaUsuario, {
        email: email || null,
      }),
    onSuccess: () => invalidarConta(cliente),
  });
}

export function useDesvincularGeoTask() {
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: (usuarioId: string) =>
      apagar(`/usuarios/${encodeURIComponent(usuarioId)}/geotask`),
    onSuccess: () => invalidarConta(cliente),
  });
}

export function useExcecoesDoUsuario(usuarioId: string) {
  return useQuery({
    queryKey: ["usuarios", "excecoes", usuarioId],
    queryFn: ({ signal }) =>
      pedirJson(`/usuarios/${encodeURIComponent(usuarioId)}/permissoes`, z.array(esquemaExcecao), { signal }),
  });
}

export function useDefinirExcecoes() {
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: ({ usuarioId, excecoes }: { usuarioId: string; excecoes: Array<{ permissao_chave: string; efeito: "grant" | "revoke"; motivo: string | null }> }) =>
      atualizarJson(`/usuarios/${encodeURIComponent(usuarioId)}/permissoes`, z.array(esquemaExcecao), { excecoes }),
    onSuccess: () => {
      invalidarConta(cliente);
      void cliente.invalidateQueries({ queryKey: ["usuarios", "excecoes"] });
    },
  });
}

export function useCancelarConvite() {
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: (conviteId: string) => apagar(`/convites/${encodeURIComponent(conviteId)}`),
    onSuccess: () => invalidarConta(cliente),
  });
}

const CATEGORIA_POR_PREFIXO: ReadonlyArray<[string, string]> = [
  ["sessao:", "Sessão"],
  ["convite:", "Conta"],
  ["usuario:", "Conta"],
  ["mfa:", "Conta"],
  ["seguranca:", "Conta"],
  ["perfil:", "Conta"],
  ["kickoff:", "Implantação"],
  ["infra:", "Implantação"],
  ["implantacao:", "Implantação"],
  ["repasse:", "Implantação"],
  ["agenda:", "Agenda"],
  ["reuniao:", "Agenda"],
  ["ata:", "Agenda"],
  ["midia:", "Materiais"],
  ["financeiro:", "Financeiro"],
  ["geoedu:", "GEOedu"],
  ["ajuda:", "Ajuda"],
  ["assistente:", "Assistente"],
  ["chamado:", "Chamados"],
  ["ticket:", "Chamados"],
  ["conhecimento:", "Base de conhecimento"],
  ["automacao:", "Automação"],
  ["organizacao:", "Conta"],
  ["cadastro:", "Conta"],
  ["ativo:", "Chamados"],
  ["exportacao:", "Chamados"],
  ["migracao:", "Migração"],
  ["painel:", "Painéis"],
  ["ia:", "Chamados"],
];

export function categoriaDoRegistro(acao: string): string {
  return CATEGORIA_POR_PREFIXO.find(([prefixo]) => acao.startsWith(prefixo))?.[1] ?? "Outros";
}

export const CATEGORIAS_DO_HISTORICO = [
  "Todas",
  "Sessão",
  "Implantação",
  "Agenda",
  "Materiais",
  "GEOedu",
  "Ajuda",
  "Assistente",
  "Chamados",
  "Base de conhecimento",
  "Automação",
  "Migração",
  "Financeiro",
  "Conta",
  "Outros",
] as const;

/**
 * A frase que o cliente lê no histórico para cada ação que a API grava na auditoria.
 *
 * A chave técnica (`usuario:papeis_definidos`) é vocabulário nosso; o histórico é lido pela
 * empresa. Toda ação gravada em `api/app` tem frase aqui — `usuarios.test.ts` confere a lista
 * contra o código da API, para uma ação nova não voltar a aparecer em forma técnica.
 */
export const FRASE_DA_ACAO: Readonly<Record<string, string>> = {
  "contato:incluido": "Contato de acesso incluído na implantação",
  "contato:alterado": "Contato de acesso corrigido",
  "contato:convite_reenviado": "Acesso do contato reenviado",
  "implantacao:excluida": "Implantação excluída do portal",
  "sessao:aberta": "Entrou no portal",
  "sessao:encerrada": "Saiu do portal",
  "sessao:recusada": "Tentativa de entrada recusada",
  "sessao:bloqueada_por_tentativas": "Entrada bloqueada por excesso de tentativas",
  "sessao:recusada_por_rede": "Acesso recusado porque o endereço não está entre as redes permitidas",
  "sessao:mfa_exigida": "Entrada aguardando o segundo fator",
  "sessao:mfa_recusada": "Código da verificação em duas etapas recusado",
  "sessao:encerrada_por_outra": "Sessão encerrada a partir da lista de sessões",
  "sessao:encerradas_em_lote": "Várias sessões encerradas de uma vez",
  "mfa:ativado": "Verificação em duas etapas ligada",
  "mfa:desativado": "Verificação em duas etapas desligada",
  "mfa:codigo_recusado": "Código da verificação em duas etapas recusado na conta",
  "mfa:codigo_de_recuperacao_usado": "Código de recuperação usado para entrar",
  "mfa:codigos_regerados": "Códigos de recuperação trocados por um conjunto novo",
  "mfa:redefinido_pelo_admin": "Verificação em duas etapas redefinida por quem administra o acesso",
  "seguranca:politica_alterada": "Política de acesso da organização alterada",
  "perfil:criado": "Perfil de acesso criado",
  "perfil:alterado": "Perfil de acesso alterado",
  "perfil:apagado": "Perfil de acesso apagado",
  "sso:sessao_aberta": "Entrou pelo acesso único",
  "sso:recusado": "Entrada pelo acesso único recusada",
  "senha:redefinida": "Senha redefinida",
  "recuperacao:emitida": "Link para criar nova senha enviado",
  "recuperacao:limitada": "Pedido de nova senha barrado por excesso de tentativas",
  "recuperacao:pedida_para_desconhecido": "Nova senha pedida para um e-mail sem cadastro",
  "convite:criado": "Convite enviado",
  "convite:aceito": "Convite aceito",
  "convite:cancelado": "Convite cancelado",
  "usuario:alterado": "Dados de um usuário alterados",
  "usuario:geotask_vinculado": "Usuário vinculado ao GEOtask",
  "usuario:geotask_desvinculado": "Vínculo com o GEOtask removido",
  "usuario:papeis_definidos": "Perfil de acesso de um usuário alterado",
  "usuario:excecoes_definidas": "Permissões extras de um usuário alteradas",
  "usuario:telas_ajustadas": "Telas de uma pessoa ajustadas",
  "usuario:desativado": "Usuário desativado",
  "organizacao:criada": "Empresa cadastrada",
  "organizacao:alterada": "Dados da empresa alterados",
  "organizacao:apagada": "Empresa removida",
  "organizacao:alias_registrado": "Outro nome da empresa registrado",
  "organizacao:alias_removido": "Outro nome da empresa removido",
  "implantacao:criada": "Implantação aberta",
  "implantacao:contrato_anexado": "Contrato assinado anexado",
  "implantacao:contrato_substituido": "Contrato assinado substituído",
  "implantacao:consultor_definido": "Consultor da implantação definido",
  "implantacao:ambiente_liberado": "Ambiente liberado",
  "implantacao:evento_registrado": "Andamento da implantação registrado",
  "implantacao:modulo_repassado": "Módulo concluído e repassado ao atendimento",
  "repasse:jira_reprocessado": "Pendência do repasse no Jira reprocessada",
  "repasse:emails_reenviados": "Formalização do repasse reenviada por e-mail",
  "recurso_plus:criado": "Recurso Plus adicionado",
  "recurso_plus:alterado": "Recurso Plus alterado",
  "kickoff:rascunho_salvo": "Respostas do kickoff salvas",
  "kickoff:trilha_enviada": "Formulário do kickoff enviado",
  "kickoff:anexo_enviado": "Arquivo anexado a uma resposta do kickoff",
  "kickoff:anexo_removido": "Arquivo removido de uma resposta do kickoff",
  "kickoff:pendencia_apontada": "Pendência apontada no kickoff",
  "kickoff:concluido": "Kickoff concluído no portal",
  "kickoff:aceito": "Kickoff aceito pela GEOvendas",
  "pacote:gerado": "Resumo do kickoff gerado",
  "pacote:issue_de_kickoff_criada": "Tarefa de Kick Off criada no Jira",
  "pacote:issue_de_kickoff_pendente": "Criação da tarefa de Kick Off no Jira pendente",
  "pacote:nao_gerado_no_aceite": "Resumo do kickoff não gerado no aceite",
  "cofre:segredo_guardado": "Credencial guardada no cofre",
  "cofre:segredo_rotacionado": "Credencial do cofre trocada",
  "cofre:segredo_descartado": "Credencial do cofre descartada",
  "cofre:segredo_revelado": "Credencial do cofre revelada",
  "agenda:consultada": "Agenda consultada",
  "reuniao:agendada": "Reunião agendada",
  "reuniao:remarcada": "Reunião remarcada",
  "reuniao:realizada": "Reunião registrada como realizada",
  "reuniao:cancelada": "Reunião cancelada",
  "reuniao:entrada": "Entrada na reunião pelo link",
  "ata:rascunho_aberto": "Ata iniciada",
  "ata:editada": "Ata editada",
  "ata:item_criado": "Item incluído na ata",
  "ata:item_editado": "Item da ata alterado",
  "ata:item_removido": "Item da ata removido",
  "ata:publicada": "Ata publicada",
  "ata:pendencia_concluida": "Pendência da ata concluída",
  "ata:pendencia_sem_tarefa": "Pendência da ata registrada sem tarefa",
  "ata:pendencia_vinculada_ao_jira": "Pendência da ata enviada como tarefa",
  "midia:enviada": "Gravação ou transcrição enviada",
  "midia:enfileirada": "Processamento da gravação iniciado",
  "midia:reprocessada": "Gravação processada de novo",
  "midia:removida": "Gravação ou transcrição removida",
  "questionario:pergunta_alterada": "Pergunta do questionário alterada",
  "questionario:pergunta_incluida": "Pergunta incluída no questionário",
  "questionario:pergunta_removida": "Pergunta removida do questionário",
  "questionario:reordenado": "Perguntas do questionário reordenadas",
  "questionario:publicado": "Questionário publicado",
  "questionario:despublicado": "Questionário despublicado",
  "ticket:aberto": "Chamado aberto",
  "ticket:respondido": "Chamado respondido",
  "ticket:nota_interna": "Nota interna registrada em um chamado",
  "ticket:alterado": "Chamado atualizado",
  "ticket:anexo_adicionado": "Arquivo anexado a um chamado",
  "ticket:jira_reprocessado": "Issue do chamado reprocessada no ATD",
  "ticket:categoria_criada": "Categoria de chamados criada",
  "ticket:servico_criado": "Serviço de chamados criado",
  "ticket:equipe_criada": "Equipe de atendimento criada",
  "ticket:sla_alerta_reconhecido": "Alerta de prazo de chamado reconhecido",
  "ticket:sla_automatico_restaurado": "Prazo automático de um chamado restaurado",
  "ticket:sla_calendario_criado": "Calendário de atendimento criado",
  "ticket:sla_contrato_criado": "Contrato de atendimento criado",
  "ticket:sla_politica_criada": "Política de prazo de atendimento criada",
  "ticket:sla_sobrescrito": "Prazo de um chamado ajustado",
  "ticket:alterado_em_lote": "Chamados atualizados em lote",
  "ticket:avaliado": "Atendimento de um chamado avaliado",
  "ticket:participante_adicionado": "Pessoa incluída em um chamado",
  "ticket:participante_removido": "Pessoa retirada de um chamado",
  "ticket:reaberto": "Chamado reaberto",
  "ticket:solucao_aceita": "Solução de um chamado aceita",
  "ticket:tag_adicionada": "Etiqueta adicionada a um chamado",
  "ticket:tag_removida": "Etiqueta retirada de um chamado",
  "ticket:campo_criado": "Campo personalizado de chamado criado",
  "ticket:campo_alterado": "Campo personalizado de chamado alterado",
  "ticket:campos_atualizados": "Campos personalizados de um chamado preenchidos",
  "ticket:relacao_adicionada": "Chamados relacionados entre si",
  "ticket:relacao_removida": "Relação entre chamados desfeita",
  "ticket:mesclado": "Chamados mesclados",
  "ticket:desmesclado": "Mescla de chamados desfeita",
  "ticket:clonado": "Chamado clonado",
  "ticket:agendamento_criado": "Abertura programada de chamados criada",
  "ticket:agendamento_alterado": "Abertura programada de chamados alterada",
  "ticket:agendamento_removido": "Abertura programada de chamados removida",
  "ticket:aberto_por_agenda": "Chamado aberto por uma abertura programada",
  "ticket:visao_criada": "Visão da fila de chamados salva",
  "ticket:visao_alterada": "Visão da fila de chamados alterada",
  "ticket:visao_apagada": "Visão da fila de chamados apagada",
  "ticket:relatorio_exportado": "Relatório de atendimento exportado",
  "ticket:relatorio_filtro_salvo": "Filtro de relatório salvo",
  "ticket:relatorio_filtro_apagado": "Filtro de relatório apagado",
  "ticket:apontamento_registrado": "Horas apontadas em um chamado",
  "ticket:apontamento_excluido": "Apontamento de horas apagado de um chamado",
  "ticket:cronometro_iniciado": "Cronômetro de um chamado iniciado",
  "ticket:cronometro_pausado": "Cronômetro de um chamado pausado",
  "ticket:cronometro_retomado": "Cronômetro de um chamado retomado",
  "ticket:cronometro_parado": "Cronômetro de um chamado parado e apontado",
  "ticket:despesa_lancada": "Despesa lançada em um chamado",
  "ticket:despesa_excluida": "Despesa apagada de um chamado",
  "ticket:contrato_de_horas_criado": "Contrato de horas criado",
  "ticket:contrato_de_horas_alterado": "Contrato de horas alterado",
  "ticket:regra_de_aprovacao_criada": "Regra de aprovação de chamados criada",
  "ticket:regra_de_aprovacao_alterada": "Regra de aprovação de chamados alterada",
  "ticket:aprovacao_aprovada": "Chamado aprovado",
  "ticket:aprovacao_recusada": "Chamado recusado na aprovação",
  "conhecimento:importacao": "Artigos importados para a base de conhecimento",
  "conhecimento:artigo_criado": "Artigo da base de conhecimento criado",
  "conhecimento:artigo_criado_de_chamado": "Artigo criado a partir de um chamado",
  "conhecimento:artigo_editado": "Artigo da base de conhecimento editado",
  "conhecimento:categoria_criada": "Categoria da base de conhecimento criada",
  "automacao:gatilho_alterado": "Gatilho de automação alterado",
  "automacao:gatilho_criado": "Gatilho de automação criado",
  "automacao:gatilho_excluido": "Gatilho de automação excluído",
  "automacao:macro_alterada": "Macro de atendimento alterada",
  "automacao:macro_aplicada": "Macro aplicada a um chamado",
  "automacao:macro_criada": "Macro de atendimento criada",
  "automacao:macro_removida": "Macro de atendimento removida",
  "automacao:token_criado": "Chave de acesso à API criada",
  "automacao:token_revogado": "Chave de acesso à API revogada",
  "automacao:webhook_alterado": "Envio automático a outro sistema alterado",
  "automacao:webhook_criado": "Envio automático a outro sistema criado",
  "automacao:webhook_excluido": "Envio automático a outro sistema excluído",
  "painel:criado": "Painel da equipe criado",
  "painel:alterado": "Painel da equipe alterado",
  "painel:apagado": "Painel da equipe apagado",
  "assistente:pergunta": "Pergunta ao assistente do portal",
  "ia:resumo": "Resumo por IA usado em um chamado",
  "ia:sugestao_de_resposta": "Rascunho de resposta por IA usado em um chamado",
  "ia:classificacao": "Classificação por IA usada em um chamado",
  "migracao:execucao_concluida": "Importação do Movidesk concluída",
  "migracao:execucao_falhou": "Importação do Movidesk falhou",
  "migracao:simulacao_concluida": "Simulação da importação do Movidesk concluída",
  "automacao:webhook_reenvio": "Envio automático a outro sistema reenviado",
  "automacao:webhook_segredo_girado": "Segredo de um envio automático trocado",
  "canal:caixa_criada": "Caixa de entrada de e-mail criada",
  "canal:caixa_alterada": "Caixa de entrada de e-mail alterada",
  "canal:caixa_token_girado": "Chave de entrada de uma caixa de e-mail trocada",
  "canal:dominio_cadastrado": "Domínio de e-mail da empresa cadastrado",
  "canal:dominio_removido": "Domínio de e-mail da empresa removido",
  "canal:formulario_criado": "Formulário público de abertura de chamado criado",
  "canal:formulario_alterado": "Formulário público de abertura de chamado alterado",
  "canal:formulario_revogado": "Formulário público de abertura de chamado desativado",
  "canal:formulario_token_girado": "Link do formulário público de abertura trocado",
  "canal:email_abriu_chamado": "E-mail recebido abriu um chamado",
  "canal:email_na_conversa": "E-mail recebido entrou na conversa de um chamado",
  "canal:email_em_quarentena": "E-mail recebido foi para a quarentena",
  "canal:email_ignorado": "E-mail automático ou repetido ignorado",
  "canal:quarentena_liberada": "E-mail da quarentena liberado pela equipe",
  "canal:quarentena_descartada": "E-mail da quarentena descartado pela equipe",
  "ticket:distribuicao_configurada": "Distribuição automática de uma equipe configurada",
  "ticket:sinalizador_definido": "Sinalizador colocado em um chamado",
  "ticket:sinalizador_retirado": "Sinalizador retirado de um chamado",
  "ticket:impresso_com_notas_internas": "Chamado impresso com as notas internas",
  "ticket:servico_acesso_definido": "Quem usa um serviço do catálogo alterado",
  "exportacao:pedida": "Exportação em segundo plano pedida",
  "exportacao:baixada": "Arquivo de uma exportação baixado",
  "organizacao:contato_criado": "Contato da empresa anotado",
  "organizacao:contato_alterado": "Contato da empresa alterado",
  "organizacao:contato_removido": "Contato da empresa removido",
  "organizacao:endereco_criado": "Endereço da empresa anotado",
  "organizacao:endereco_alterado": "Endereço da empresa alterado",
  "organizacao:endereco_removido": "Endereço da empresa removido",
  "organizacao:fundida": "Empresa duplicada fundida nesta",
  "usuario:fundido": "Pessoa duplicada fundida nesta",
  "cadastro:importacao_aplicada": "Planilha de cadastro importada",
  "ativo:criado": "Ativo cadastrado",
  "ativo:alterado": "Ativo alterado",
  "ativo:pessoas_definidas": "Pessoas de um ativo alteradas",
  "ativo:chamados_definidos": "Ativos de um chamado alterados",
  "aviso:publicado": "Aviso publicado no mural",
  "feedback:criado": "Demanda publicada no mural de feedback",
  "feedback:editado": "Demanda do mural de feedback editada",
  "feedback:estado": "Estado da demanda do mural de feedback alterado",
  "aviso:alterado": "Aviso do mural alterado",
  "aviso:arquivado": "Aviso do mural arquivado",
  "pesquisa:criada": "Pesquisa de satisfação criada",
  "pesquisa:alterada": "Pesquisa de satisfação alterada",
  "pesquisa:link_consultado": "Link de pesquisa de satisfação consultado",
  "pesquisa:envio_reenviado": "Convite de pesquisa de satisfação reenviado",
  "webchat:configurado": "Chat de uma empresa configurado",
  "webchat:conversa_aceita": "Conversa do chat aceita pela equipe",
  "webchat:conversa_transferida": "Conversa do chat transferida",
  "webchat:conversa_encerrada": "Conversa do chat encerrada e registrada em chamado",
  "sucesso:configuracao_alterada": "Pesos da saúde do cliente alterados",
  "sucesso:responsavel_definido": "Responsável de CS da empresa definido",
  "sucesso:recalculado": "Saúde dos clientes recalculada",
  "compromisso:criado": "Compromisso agendado",
  "compromisso:alterado": "Compromisso alterado",
  "compromisso:remarcado": "Compromisso remarcado",
  "compromisso:concluido": "Compromisso concluído",
  "compromisso:cancelado": "Compromisso cancelado",
};

/** A ação em frase; o que a API passar a gravar sem frase aqui cai numa fala neutra, nunca na chave. */
export function fraseDaAcao(acao: string): string {
  return FRASE_DA_ACAO[acao] ?? "Outra ação registrada na conta";
}
